import {IncomingHttpHeaders} from 'http2'
import {HttpError, HttpMethod, RequestData, ResponseError, StatusCode, Body} from './http'
import _ from 'lodash'

export default class Api {
  baseUrl: string
  headers: IncomingHttpHeaders
  extraQueryParams: object

  /**
   * @param baseUrl the base api url
   * @param headers the headers to use for requests
   * @param extraQueryParams additional params to include in the url
   */
  constructor(baseUrl: string, headers: IncomingHttpHeaders, extraQueryParams: object = {}) {
    this.headers = headers
    this.baseUrl = baseUrl
    this.extraQueryParams = extraQueryParams
  }

  /**
   * Makes a GET request to the api
   * @param endpoint the endpoint to use
   */
  async get(endpoint: string) {
    return await this.request({method: HttpMethod.Get, endpoint})
  }
  /**
   * Makes a PATCH request to the api
   * @param endpoint the endpoint to use
   * @param body the body to use
   */
  async patch(endpoint: string, body?: Body) {
    return await this.request({method: HttpMethod.Patch, endpoint, body})
  }

  /**
   * Makes a POST request to the api
   * @param endpoint the endpoint to use
   * @param json the json body
   */
  async postJson(endpoint: string, json?: object) {
    return await this.request({method: HttpMethod.Post, endpoint, json})
  }

  /**
   * Makes a POST request to the api
   * @param endpoint the endpoint to use
   * @param body the body to use
   */
  async post(endpoint: string, body?: Body) {
    return await this.request({method: HttpMethod.Post, endpoint, body})
  }

  /**
   * Makes a PUT request to the api
   * @param endpoint the endpoint to use
   * @param body the body to use
   */
  async put(endpoint: string, body?: Body) {
    return await this.request({method: HttpMethod.Put, endpoint, body})
  }

  /**
   * Makes a PUT request to the api
   * @param endpoint the endpoint to use
   * @param json the json body
   */
  async putJson(endpoint: string, json?: object) {
    return await this.request({method: HttpMethod.Put, endpoint, json})
  }

  /**
   * Makes a DELETE request to the api
   * @param endpoint the endpoint to use
   * @param body the body to use
   */
  async delete(endpoint: string, body?: Body) {
    return await this.request({method: HttpMethod.Delete, endpoint, body})
  }

  /**
   * Makes a DELETE request to the api
   * @param endpoint the endpoint to use
   * @param json the json body
   */
  async deleteJson(endpoint: string, json?: object) {
    return await this.request({method: HttpMethod.Delete, endpoint, json})
  }

  /** @private */
  private async request({method, endpoint, json, body}: RequestData) {
    const params: any = {
      method,
      headers: {
        Accept: 'application/json',
        ...this.headers,
      },
    }
    if (json) {
      params.headers['Content-Type'] = 'application/json'
      params.body = JSON.stringify(json)
    }
    if (body) {
      params.body = body
    }
    const url = this.buildUrl(endpoint)
    console.log(method, url)
    let response: Partial<Response> = {}
    try {
      response = await fetch(url, params)
    } catch (_error) {}
    console.log(method, url, response.status)
    if (response.ok) {
      const contentType = response.headers?.get('Content-type') || ''
      const isJson = _.includes(contentType, 'application/json')
      try {
        if (response.status !== StatusCode.NoContent && isJson) {
          const text = await (response as Response).text()
          return JSON.parse(text)
        }
      } catch (error) {
        throw new ResponseError(Error('Invalid json'), response)
      }
      return
    }

    if (response.status === StatusCode.Unauthorized) {
      throw new ResponseError(HttpError.Unauthorized, response)
    }
    if (response.status === StatusCode.Forbidden) {
      throw new ResponseError(HttpError.Forbidden, response)
    }
    let errorMessage = null
    try {
      const responseBody = await (response as Response).text()
      errorMessage = responseBody
    } catch (error) {}
    errorMessage = errorMessage || response.statusText
    if (errorMessage) {
      throw new ResponseError(Error(errorMessage), response)
    } else {
      throw new ResponseError(HttpError.Server, response)
    }
  }

  /** @private */
  private buildUrl(endpoint: string) {
    const paramString = _.entries(this.extraQueryParams)
      .map(([name, value]) => `${name}=${encodeURI(value)}`)
      .join('&')
    let prefix = ''
    if (_.includes(endpoint, '?')) {
      prefix = '&'
    } else if (!_.isEmpty(paramString)) {
      prefix = '?'
    }
    if (!_.isEmpty(paramString)) {
      endpoint = endpoint.concat(`${prefix}${paramString}`)
    }
    return `${this.baseUrl}${endpoint}`
  }
}
